import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import {
  AdminBlogCategory,
  BlogCategoryCreateRequest,
  BlogCategoryEditData,
  BlogCategoryFormDialogData
} from '@app/model/blog-management.model';
import { environment } from '@app/../environments/environment';
import { BlogManagementDataService } from '@app/services/blog-management-data.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { of, throwError } from 'rxjs';

interface BlogCategoryLanguage {
  code: string;
  label: string;
}

@Component({
  selector: 'meu-blog-category-form',
  templateUrl: './blog-category-form.component.html',
  styleUrls: ['./blog-category-form.component.scss']
})
export class BlogCategoryFormComponent implements OnInit {
  isSaving = false;
  isLoadingData = false;
  parentCategories: AdminBlogCategory[] = [];
  selectedLanguageIndex = 0;

  selectedImageFile: File | null = null;
  imagePreviewUrl = '';

  languages: BlogCategoryLanguage[] = [
    {
      code: 'vi',
      label: 'Tiếng Việt'
    },
    {
      code: 'en',
      label: 'English'
    },
    {
      code: 'zh',
      label: '中文'
    }
  ];

  languageForms: {
    [languageCode: string]: FormGroup;
  } = {
    vi: this.createLanguageForm(),
    en: this.createLanguageForm(),
    zh: this.createLanguageForm()
  };

  settingsForm = new FormGroup({
    code: new FormControl('', [Validators.required]),
    parentId: new FormControl(''),
    priority: new FormControl(0, [Validators.required, Validators.min(0)]),
    image: new FormControl(''),
    isHidden: new FormControl(false)
  });

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: BlogCategoryFormDialogData,
    private dialogRef: MatDialogRef<BlogCategoryFormComponent>,
    private blogManagementDataService: BlogManagementDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.parentCategories = this.getAvailableParentCategories();

    if (this.data.mode === 'edit' && this.data.category) {
      this.loadCategoryForEdit();
    }
  }

  get modalTitle(): string {
    return this.data.mode === 'edit' ? 'Sửa danh mục' : 'Thêm danh mục';
  }

  get submitLabel(): string {
    if (this.isSaving) {
      return 'Đang lưu...';
    }

    return this.data.mode === 'edit' ? 'Lưu thay đổi' : 'Tạo danh mục';
  }

  closeModal(): void {
    if (this.isSaving) {
      return;
    }

    this.dialogRef.close();
  }

  saveCategory(): void {
    if (this.isLoadingData || this.isSaving) {
      return;
    }
    const vietnameseForm = this.languageForms.vi;
    const englishForm = this.languageForms.en;
    const chineseForm = this.languageForms.zh;

    const languageFormEntries = [
      {
        index: 0,
        label: 'Tiếng Việt',
        form: vietnameseForm
      },
      {
        index: 1,
        label: 'English',
        form: englishForm
      },
      {
        index: 2,
        label: '中文',
        form: chineseForm
      }
    ];

    const invalidLanguage = languageFormEntries.find((language) => language.form.invalid);

    if (invalidLanguage || this.settingsForm.invalid) {
      languageFormEntries.forEach((language) => {
        language.form.markAllAsTouched();
      });

      this.settingsForm.markAllAsTouched();

      if (invalidLanguage) {
        this.selectedLanguageIndex = invalidLanguage.index;

        this.toastr.error('Vui lòng nhập tên danh mục trong tab ' + invalidLanguage.label);

        return;
      }

      this.toastr.error('Vui lòng nhập đầy đủ thiết lập danh mục');

      return;
    }

    const vietnameseContent = vietnameseForm.value;
    const englishContent = englishForm.value;
    const chineseContent = chineseForm.value;
    const settings = this.settingsForm.value;
    const selectedParentId = this.settingsForm.get('parentId').value;

    const payload: BlogCategoryCreateRequest = {
      category: {
        code: (settings.code || '').trim(),
        name: (englishContent.name || '').trim(),
        slug: (englishContent.slug || '').trim().toLowerCase(),
        image: (settings.image || '').trim(),
        priority: Number(settings.priority) || 0,
        is_hide: settings.isHidden === true,
        parent_id: selectedParentId || null
      },
      translations: [
        {
          language_code: 'vi',
          name: (vietnameseContent.name || '').trim(),
          slug: (vietnameseContent.slug || '').trim().toLowerCase()
        },
        {
          language_code: 'zh',
          name: (chineseContent.name || '').trim(),
          slug: (chineseContent.slug || '').trim().toLowerCase()
        }
      ]
    };
    this.submitCategory(payload);
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      return;
    }

    const imageFile = input.files[0];

    if (imageFile.type.indexOf('image/') !== 0) {
      this.toastr.error('Vui lòng chọn đúng định dạng hình ảnh');
      input.value = '';

      return;
    }

    this.selectedImageFile = imageFile;

    const reader = new FileReader();

    reader.onload = () => {
      this.imagePreviewUrl = typeof reader.result === 'string' ? reader.result : '';
    };

    reader.readAsDataURL(imageFile);
  }

  private getCategoryImageUrl(imagePath: string): string {
    if (!imagePath) {
      return '';
    }

    if (imagePath.indexOf('http') === 0) {
      return imagePath;
    }

    return environment.backendhost + imagePath;
  }

  private getAvailableParentCategories(): AdminBlogCategory[] {
    if (this.data.mode !== 'edit' || !this.data.category) {
      return this.data.categories;
    }

    const unavailableIds = new Set<string>();

    this.collectChildCategoryIds(this.data.category.id, unavailableIds);

    unavailableIds.add(this.data.category.id);

    return this.data.categories.filter((category) => !unavailableIds.has(category.id));
  }

  private collectChildCategoryIds(parentId: string, categoryIds: Set<string>): void {
    this.data.categories
      .filter((category) => category.parentId === parentId)
      .forEach((category) => {
        if (categoryIds.has(category.id)) {
          return;
        }

        categoryIds.add(category.id);

        this.collectChildCategoryIds(category.id, categoryIds);
      });
  }

  private loadCategoryForEdit(): void {
    if (!this.data.category) {
      return;
    }

    this.isLoadingData = true;

    this.blogManagementDataService
      .getCategoryForEdit(this.data.category)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingData = false;
        })
      )
      .subscribe(
        (category) => {
          this.patchCategoryForm(category);
        },
        () => {
          this.toastr.error('Không thể tải dữ liệu danh mục');
        }
      );
  }

  private patchCategoryForm(category: BlogCategoryEditData): void {
    this.settingsForm.patchValue({
      code: category.code,
      parentId: category.parentId || '',
      priority: category.priority,
      image: category.image,
      isHidden: category.isHidden
    });

    this.imagePreviewUrl = this.getCategoryImageUrl(category.image);

    this.languageForms.vi.patchValue(category.languageContents.vi);

    this.languageForms.en.patchValue(category.languageContents.en);

    this.languageForms.zh.patchValue(category.languageContents.zh);
  }

  private createLanguageForm(): FormGroup {
    return new FormGroup({
      name: new FormControl('', [Validators.required]),
      slug: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      ])
    });
  }

  private submitCategory(payload: BlogCategoryCreateRequest): void {
    const category = this.data.category;

    const request$ =
      this.data.mode === 'edit' && category
        ? this.blogManagementDataService.updateCategory(category.id, payload)
        : this.blogManagementDataService.createCategory(payload);

    this.isSaving = true;

    request$
      .pipe(
        take(1),

        switchMap((response) => {
          if (!response || !response.success) {
            return of(response);
          }

          if (!this.selectedImageFile) {
            return of(response);
          }

          const categoryId =
            category && category.id
              ? category.id
              : response.data && response.data.id
                ? response.data.id
                : '';

          if (!categoryId) {
            return throwError({
              isImageUploadError: true,
              message: 'Danh mục đã được lưu nhưng không tìm thấy ID để tải ảnh'
            });
          }

          return this.blogManagementDataService
            .uploadCategoryImage(categoryId, this.selectedImageFile)
            .pipe(
              take(1),

              switchMap((uploadResponse) => {
                if (!uploadResponse || !uploadResponse.success) {
                  return throwError({
                    isImageUploadError: true,
                    message:
                      uploadResponse && uploadResponse.message
                        ? uploadResponse.message
                        : 'Danh mục đã được lưu nhưng không thể tải ảnh'
                  });
                }

                return of(response);
              }),

              catchError((error) => {
                if (error && error.isImageUploadError) {
                  return throwError(error);
                }

                const errorResponse = error && error.error ? error.error : null;

                return throwError({
                  isImageUploadError: true,
                  message:
                    errorResponse && errorResponse.message
                      ? errorResponse.message
                      : 'Danh mục đã được lưu nhưng không thể tải ảnh'
                });
              })
            );
        }),

        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể lưu danh mục'
            );

            return;
          }

          const isEditing = this.data.mode === 'edit';

          this.toastr.success(
            isEditing ? 'Cập nhật danh mục thành công' : 'Tạo danh mục thành công'
          );

          this.dialogRef.close({
            created: !isEditing,
            updated: isEditing
          });
        },

        (error) => {
          if (error && error.isImageUploadError) {
            this.toastr.error(error.message);
            return;
          }

          const errorResponse = error && error.error ? error.error : null;

          let message = 'Không thể lưu danh mục';

          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }

          this.toastr.error(message);
        }
      );
  }
}
